import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react"
import { cn } from "@/shared/lib/cn"
import { ChevronDown } from "@/shared/ui/icons"

const FIELD_BASE =
  "w-full h-[52px] px-4 rounded-xl border border-verde-profundo/20 bg-white text-verde-profundo text-base placeholder:text-verde-profundo/40 focus:outline-none focus:border-dourado focus:ring-2 focus:ring-dourado/20 transition-colors"

interface FieldShellProps {
  label: string
  id: string
  error?: string
  /** Texto de apoio sob o campo. Sai de cena quando ha erro. */
  hint?: string
  /** Marca o campo como dispensavel — o padrao do site e campo obrigatorio. */
  optional?: boolean
  className?: string
  children: ReactNode
}

/** Label + campo + apoio/erro. O que todos os campos abaixo tem em comum. */
function FieldShell({ label, id, error, hint, optional, className, children }: FieldShellProps) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={id} className="text-sm font-medium text-verde-profundo">
        {label}
        {optional ? <span className="ml-1.5 font-normal text-verde-profundo/40">(opcional)</span> : null}
      </label>

      {children}

      {error ? (
        <p id={`${id}-error`} className="text-xs text-red-500">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-verde-profundo/45 leading-relaxed">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

/** `aria-describedby` aponta para o erro quando existe, senao para a dica. */
function describedBy(id: string, error?: string, hint?: string): string | undefined {
  if (error) return `${id}-error`
  if (hint) return `${id}-hint`
  return undefined
}

export interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  id: string
  error?: string
  hint?: string
  optional?: boolean
  fieldClassName?: string
}

export function FormField({
  label,
  id,
  error,
  hint,
  optional,
  className,
  fieldClassName,
  ...rest
}: FormFieldProps) {
  return (
    <FieldShell label={label} id={id} error={error} hint={hint} optional={optional} className={fieldClassName}>
      <input
        id={id}
        // Sem estes dois, um leitor de tela anuncia o campo como valido e nunca
        // le a mensagem de erro que esta logo abaixo.
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        className={cn(FIELD_BASE, error && "border-red-400", className)}
        {...rest}
      />
    </FieldShell>
  )
}

export interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string
  id: string
  error?: string
  hint?: string
  optional?: boolean
  fieldClassName?: string
  children: ReactNode
}

export function SelectField({
  label,
  id,
  error,
  hint,
  optional,
  children,
  className,
  fieldClassName,
  ...rest
}: SelectFieldProps) {
  return (
    <FieldShell label={label} id={id} error={error} hint={hint} optional={optional} className={fieldClassName}>
      <div className="relative">
        <select
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, error, hint)}
          className={cn(FIELD_BASE, "appearance-none pr-10 cursor-pointer", error && "border-red-400", className)}
          {...rest}
        >
          {children}
        </select>
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-verde-profundo/50">
          <ChevronDown />
        </span>
      </div>
    </FieldShell>
  )
}

export interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string
  id: string
  error?: string
  hint?: string
  optional?: boolean
  fieldClassName?: string
}

/**
 * Campo de texto longo.
 *
 * Existe para os blocos da ficha de credenciamento em que a resposta e uma
 * frase e nao um dado — horarios de funcionamento, condicoes negociadas,
 * observacoes. Num `input` de uma linha esse texto fica ilegivel assim que
 * passa de meia dezena de palavras.
 */
export function TextAreaField({
  label,
  id,
  error,
  hint,
  optional,
  className,
  fieldClassName,
  rows = 3,
  ...rest
}: TextAreaFieldProps) {
  return (
    <FieldShell label={label} id={id} error={error} hint={hint} optional={optional} className={fieldClassName}>
      <textarea
        id={id}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        className={cn(
          FIELD_BASE,
          // A altura fixa do FIELD_BASE e de campo de uma linha.
          "h-auto min-h-[52px] py-3 leading-relaxed resize-y",
          error && "border-red-400",
          className,
        )}
        {...rest}
      />
    </FieldShell>
  )
}

/* ── Escolha: caixa e radio ───────────────────────────────────────────────── */

const CHOICE_BASE =
  "flex items-start gap-3 p-3.5 rounded-xl border bg-white cursor-pointer transition-colors select-none has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-dourado"

const CHOICE_OFF = "border-verde-profundo/15 hover:border-verde-profundo/35"
const CHOICE_ON = "border-dourado bg-dourado/8"

export interface ChoiceProps {
  id: string
  name?: string
  label: string
  description?: string
  checked: boolean
  onChange: (checked: boolean) => void
  className?: string
}

/**
 * Item de escolha desenhado como cartao.
 *
 * O `input` nativo fica escondido, e nao substituido: teclado, leitor de tela,
 * `Tab`, `Espaco` e o clique no rotulo continuam sendo os do navegador. O
 * quadrado desenhado ao lado e so pintura — nada nele participa da semantica.
 */
function Choice({
  type,
  id,
  name,
  label,
  description,
  checked,
  onChange,
  className,
}: ChoiceProps & { type: "checkbox" | "radio" }) {
  return (
    <label htmlFor={id} className={cn(CHOICE_BASE, checked ? CHOICE_ON : CHOICE_OFF, className)}>
      <input
        type={type}
        id={id}
        name={name}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="sr-only"
      />

      <span
        aria-hidden="true"
        className={cn(
          "mt-0.5 shrink-0 w-[18px] h-[18px] border-2 flex items-center justify-center transition-colors",
          type === "radio" ? "rounded-full" : "rounded-[5px]",
          checked ? "border-dourado bg-dourado text-white" : "border-verde-profundo/25 bg-white",
        )}
      >
        {checked ? (
          type === "radio" ? (
            <span className="w-1.5 h-1.5 rounded-full bg-white" />
          ) : (
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path
                d="M2.5 6.2 4.8 8.5 9.5 3.8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )
        ) : null}
      </span>

      <span className="flex flex-col gap-0.5 min-w-0">
        <span className="text-sm font-medium text-verde-profundo leading-snug">{label}</span>
        {description ? (
          <span className="text-xs text-verde-profundo/50 leading-relaxed">{description}</span>
        ) : null}
      </span>
    </label>
  )
}

export function CheckboxField(props: ChoiceProps) {
  return <Choice type="checkbox" {...props} />
}

export function RadioField(props: ChoiceProps & { name: string }) {
  return <Choice type="radio" {...props} />
}

export interface ChoiceGroupProps {
  legend: string
  error?: string
  hint?: string
  /** Id estavel para o `aria-describedby` do grupo. */
  id: string
  columns?: 1 | 2 | 3
  children: ReactNode
}

const COLUMNS: Record<1 | 2 | 3, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
}

/**
 * Conjunto de escolhas com rotulo proprio.
 *
 * E `fieldset`/`legend` de proposito: um grupo de caixas sem legenda e
 * anunciado item a item, sem a pergunta que eles respondem — quem usa leitor de
 * tela ouve "Cirurgias, caixa de selecao" e nao sabe do que se trata.
 */
export function ChoiceGroup({ legend, error, hint, id, columns = 2, children }: ChoiceGroupProps) {
  return (
    <fieldset
      id={id}
      aria-describedby={describedBy(id, error, hint)}
      aria-invalid={error ? true : undefined}
    >
      <legend className="text-sm font-medium text-verde-profundo mb-2.5">{legend}</legend>

      <div className={cn("grid gap-2.5", COLUMNS[columns])}>{children}</div>

      {error ? (
        <p id={`${id}-error`} className="text-xs text-red-500 mt-2">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-verde-profundo/45 mt-2 leading-relaxed">
          {hint}
        </p>
      ) : null}
    </fieldset>
  )
}
